import React, { useEffect, useMemo, useState } from "react";
import { formatDecimal } from "./CondicionesFinancieras";
import { pagosApi } from "./pagosApi";

export const ELEGIBILIDAD_LABELS = {
  ELEGIBLE_SIMULADO: "Elegible (simulado)",
  ELEGIBLE_DATOS_INVALIDOS: "Neto positivo · datos inválidos",
  NO_PAGA: "No paga (neto ≤ 0)",
  BLOQUEADO_INCOMPLETO: "Bloqueado: incompleto",
  SIN_BENEFICIARIO: "Sin beneficiario",
};

const ELEGIBILIDAD_CLASES = {
  ELEGIBLE_SIMULADO: "pagos-status--safe",
  NO_PAGA: "",
  ELEGIBLE_DATOS_INVALIDOS: "pagos-status--warning",
  BLOQUEADO_INCOMPLETO: "pagos-status--warning",
  SIN_BENEFICIARIO: "pagos-status--warning",
};

const COMPONENTE_LABELS = {
  COSTO_FINANCIERO: "Costo financiero", ANTICIPO: "Anticipo", ARANCEL: "Arancel", IVA: "IVA", IMPUESTO_DEBITO_CREDITO: "Débito/crédito",
  RETENCION_IIBB: "IIBB", RETENCION_GANANCIA: "Ret. Ganancias", RETENCION_IVA: "Ret. IVA", RETENCION_MUNICIPAL: "Ret. municipal",
};

const centavos = (valor) => (valor === null || valor === undefined ? "No calculable" : formatDecimal(`${valor < 0 ? "-" : ""}${Math.floor(Math.abs(valor) / 100)}.${String(Math.abs(valor) % 100).padStart(2, "0")}`));
const formatFecha = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "—";
};

function Veredicto({ valor }) {
  return <span className={`pagos-status ${valor === "VALIDADO" ? "pagos-status--safe" : "pagos-status--warning"}`}>{valor === "VALIDADO" ? "VALIDADO" : "NO VALIDADO"}</span>;
}

function Lista({ titulo, items, clase = "" }) {
  if (!items || !items.length) return null;
  return <div className={`pagos-alert ${clase}`} role="status"><strong>{titulo}</strong><ul>{items.map((item) => <li key={item}>{item}</li>)}</ul></div>;
}

function Resultado({ resultado }) {
  const [filtro, setFiltro] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [abierto, setAbierto] = useState(null);
  const grupos = useMemo(() => (resultado.grupos || []).filter((grupo) => (!filtro || grupo.elegibilidad === filtro)
    && (!busqueda || `${grupo.nombreComercio} ${grupo.razonSocial}`.toLowerCase().includes(busqueda.toLowerCase()))), [resultado, filtro, busqueda]);
  const r = resultado.resumen;
  const c = resultado.comparacion;
  const diferenciasGrupo = (c.grupos || []).filter((item) => item.estado !== "COINCIDE");

  return (
    <section aria-label="Resultado del FUR autónomo">
      <div className="pagos-section-heading">
        <div><h3>Lote {formatFecha(resultado.fechaLote)} · importación {resultado.importacion.id}</h3>
          <p>Snapshot {resultado.snapshot.archivo} · motor {resultado.motorVersion} · componentes comparados contra {resultado.fuenteComponentes === "REPORTE_DIARIO2" ? "Reporte Diario2" : "base_dashboard"}</p></div>
        <div className="pagos-header-badges"><Veredicto valor={resultado.veredicto} />
          <span className={`pagos-status ${resultado.estado === "CALCULADA" ? "pagos-status--safe" : "pagos-status--warning"}`}>{resultado.estado === "CALCULADA" ? "Calculada" : "Calculada con bloqueos"}</span></div>
      </div>

      <Lista titulo="Motivos de bloqueo" items={resultado.bloqueos} clase="pagos-alert--error" />
      <Lista titulo="Por qué no está validado" items={c.motivos} clase="pagos-alert--warning" />
      {c.diferenciasInternasCompensadas && <div className="pagos-alert pagos-alert--warning" role="alert"><strong>El total coincide, pero hay diferencias internas que se compensan.</strong> No se acepta como validación.</div>}
      <Lista titulo="Observaciones" items={c.observaciones} />
      <Lista titulo="Advertencias" items={resultado.advertencias} clase="pagos-alert--info" />

      <div className="pagos-summary pagos-summary--demo">
        <div className="pagos-summary-card"><span>Operaciones</span><strong>{r.calculadas}/{r.operaciones}</strong><small>{r.incompletas} incompletas</small></div>
        <div className="pagos-summary-card"><span>Grupos FUR</span><strong>{r.grupos}</strong><small>{r.beneficiarios} beneficiarios (CUIT) · histórico {r.historicoGrupos}</small></div>
        <div className="pagos-summary-card"><span>Bruto</span><strong>{formatDecimal(r.bruto)}</strong><small>Costos {formatDecimal(r.costos)} · retenciones {formatDecimal(r.retenciones)}</small></div>
        <div className="pagos-summary-card"><span>Neto FUR autónomo</span><strong>{r.netoElegible === null ? "Bloqueado" : formatDecimal(r.netoElegible)}</strong><small>Parcial calculado {formatDecimal(r.netoElegibleParcial)}</small></div>
        <div className="pagos-summary-card"><span>Neto FUR histórico</span><strong>{formatDecimal(r.historicoNeto)}</strong><small>Diferencia {centavos(c.diferenciaTotalCentavos)}</small></div>
        <div className="pagos-summary-card"><span>Elegibilidad</span><strong>{r.gruposElegibles}</strong>
          <small>{r.gruposNoPaga} no pagan · {r.gruposBloqueados} bloqueados · {r.gruposDatosInvalidos} con datos inválidos</small></div>
      </div>

      <h4 className="pagos-subheading">Reglas aplicadas</h4>
      <div className="pagos-table-wrap"><table aria-label="Reglas aplicadas"><thead><tr>
        <th>Regla</th><th>Tipo · alcance</th><th>Sujeto · parámetro</th><th>Tasa</th><th>Vigencia</th><th>Fuente / motivo</th><th>Operaciones</th>
      </tr></thead><tbody>
        {!(resultado.reglasAplicadas || []).length ? <tr><td colSpan="7">Sin reglas aprobadas para pruebas: se liquidó sólo con el snapshot de Listas.</td></tr>
          : resultado.reglasAplicadas.map((regla) => (
            <tr key={regla.origen}><td>{regla.origen}<small className="pagos-cell-note">Aprobó {regla.aprobadoPor}</small></td><td>{regla.tipo} · {regla.alcance}</td>
              <td>{regla.sujeto} · {regla.parametro}</td><td>{String(regla.porcentaje).replace(".", ",")} %</td>
              <td>{formatFecha(regla.desde)} → {regla.hasta ? formatFecha(regla.hasta) : "sin fin"}</td><td>{regla.fuente}<small className="pagos-cell-note">{regla.motivo}</small></td><td>{regla.operaciones}</td></tr>
          ))}
      </tbody></table></div>

      {(resultado.incompletitudes || []).length > 0 && <>
        <h4 className="pagos-subheading">Operaciones incompletas</h4>
        <div className="pagos-table-wrap"><table aria-label="Operaciones incompletas"><thead><tr><th>Motivo</th><th>Operaciones</th><th>Bruto</th></tr></thead><tbody>
          {resultado.incompletitudes.map((item) => <tr key={item.motivo}><td>{item.motivo}</td><td>{item.operaciones}</td><td>{formatDecimal(item.netoHistorico)}</td></tr>)}
        </tbody></table></div>
      </>}

      <h4 className="pagos-subheading">Grupos del FUR simulado</h4>
      <div className="pagos-filter-grid">
        <input className="pagos-search" aria-label="Buscar grupo" placeholder="Comercio o razón social" value={busqueda} onChange={(event) => setBusqueda(event.target.value)} />
        <select aria-label="Filtrar elegibilidad" value={filtro} onChange={(event) => setFiltro(event.target.value)}>
          <option value="">Todas las elegibilidades</option>
          {Object.entries(ELEGIBILIDAD_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </div>
      <div className="pagos-table-wrap"><table aria-label="Grupos FUR autónomo"><thead><tr>
        <th>Comercio</th><th>CUIT · cuenta</th><th>Operaciones</th><th>Bruto</th><th>Costos</th><th>Retenciones</th><th>Neto</th><th>Elegibilidad</th><th>Incidencias</th><th></th>
      </tr></thead><tbody>
        {grupos.map((grupo) => (
          <React.Fragment key={grupo.id}>
            <tr className={grupo.elegibilidad === "ELEGIBLE_SIMULADO" ? "" : "pagos-row--review"}>
              <td><strong>{grupo.nombreComercio}</strong><small className="pagos-cell-note">{grupo.razonSocial}</small></td>
              <td>{grupo.cuit}<small className="pagos-cell-note">{grupo.tipoCuenta} {grupo.cuenta}</small></td>
              <td>{grupo.operaciones}{grupo.incompletas > 0 && <small className="pagos-cell-note">{grupo.incompletas} incompletas</small>}</td>
              <td>{formatDecimal(grupo.bruto)}</td><td>{formatDecimal(grupo.costos)}</td><td>{formatDecimal(grupo.retenciones)}</td>
              <td>{grupo.neto === null ? "Bloqueado" : formatDecimal(grupo.neto)}</td>
              <td><span className={`pagos-status ${ELEGIBILIDAD_CLASES[grupo.elegibilidad] || ""}`}>{ELEGIBILIDAD_LABELS[grupo.elegibilidad] || grupo.elegibilidad}</span></td>
              <td><small>{(grupo.incidencias || []).map((issue) => issue.message).join(" · ") || "—"}</small></td>
              <td><button type="button" className="pagos-secondary" onClick={() => setAbierto(abierto === grupo.id ? null : grupo.id)}>{abierto === grupo.id ? "Ocultar" : "Operaciones"}</button></td>
            </tr>
            {abierto === grupo.id && <tr className="pagos-detail-row"><td colSpan="10">
              <table aria-label={`Operaciones de ${grupo.nombreComercio}`}><thead><tr><th>Operación</th><th>Fecha</th><th>Tarjeta</th><th>Bruto</th><th>Costos</th><th>Retenciones</th><th>Neto</th><th>Estado</th></tr></thead><tbody>
                {(grupo.detalle || []).map((op) => (
                  <tr key={op.id}><td>{op.id}<small className="pagos-cell-note">Terminal {op.terminal}</small></td><td>{formatFecha(op.fechaOperacion)}</td><td>{op.tarjeta} · {op.cuotas} c.</td>
                    <td>{formatDecimal(op.bruto)}</td><td>{formatDecimal(op.costos)}</td><td>{formatDecimal(op.retenciones)}</td>
                    <td>{op.estado === "INCOMPLETO" ? `Incompleta: ${(op.incompletitudes || []).join(" ")}` : formatDecimal(op.neto)}</td><td>{op.estado}</td></tr>
                ))}
              </tbody></table>
            </td></tr>}
          </React.Fragment>
        ))}
      </tbody></table></div>

      <h4 className="pagos-subheading">Comparación contra el histórico</h4>
      <div className="pagos-summary pagos-summary--demo">
        <div className="pagos-summary-card"><span>Operaciones</span><strong>{c.operaciones.netosExactos} exactas</strong><small>{c.operaciones.netosDistintos} distintas · {c.operaciones.emparejadas} emparejadas · <Veredicto valor={c.operaciones.veredicto} /></small></div>
        <div className="pagos-summary-card"><span>Grupos</span><strong>{c.gruposCoincidentes} coinciden</strong><small>{c.gruposDistintos} distintos · {c.gruposSoloAutonomo} sólo autónomo · {c.gruposSoloHistorico} sólo histórico · {c.gruposBloqueados} bloqueados</small></div>
        <div className="pagos-summary-card"><span>Cuentas</span><strong>{c.operacionesConCuentaDistinta}</strong><small>operaciones pagadas a otra cuenta en el histórico</small></div>
        <div className="pagos-summary-card"><span>Total</span><strong>{centavos(c.diferenciaTotalCentavos)}</strong><small>autónomo {c.totalAutonomo === null ? "bloqueado" : formatDecimal(c.totalAutonomo)} · histórico {formatDecimal(c.totalHistorico)}</small></div>
      </div>
      <div className="pagos-table-wrap"><table aria-label="Comparación por componente"><thead><tr>
        <th>Componente</th><th>Exactos</th><th>Distintos</th><th>Incompletos</th><th>Sin dato histórico</th><th>Diferencia</th>
      </tr></thead><tbody>
        {(c.operaciones.porComponente || []).map((item) => (
          <tr key={item.componente}><td>{COMPONENTE_LABELS[item.componente] || item.componente}</td><td>{item.exactos}</td><td>{item.distintos}</td>
            <td>{item.incompletos}</td><td>{item.noDisponibles}</td><td>{centavos(item.diferenciaCentavos)}</td></tr>
        ))}
      </tbody></table></div>

      <div className="pagos-table-wrap"><table aria-label="Diferencias por grupo"><thead><tr>
        <th>Grupo</th><th>CUIT · cuenta</th><th>Estado</th><th>Operaciones</th><th>Neto autónomo</th><th>Neto histórico</th><th>Diferencia</th>
      </tr></thead><tbody>
        {!diferenciasGrupo.length ? <tr><td colSpan="7">Todos los grupos coinciden con el FUR histórico.</td></tr>
          : diferenciasGrupo.map((item) => (
            <tr key={`${item.grupoAutonomoId || "h"}-${item.filaFurHistorica || "a"}`}><td>{item.nombreComercio}</td><td>{item.cuit}<small className="pagos-cell-note">{item.cuenta}</small></td>
              <td>{item.estado}</td><td>{item.operacionesAutonomo} / {item.operacionesHistorico}</td>
              <td>{formatDecimal(item.netoAutonomo)}</td><td>{formatDecimal(item.netoHistorico)}</td><td>{centavos(item.diferenciaCentavos)}</td></tr>
          ))}
      </tbody></table></div>

      {([...(c.porBeneficiario || []), ...(c.porCuenta || [])].length > 0) && <div className="pagos-table-wrap"><table aria-label="Diferencias por beneficiario y cuenta"><thead><tr>
        <th>Nivel</th><th>CUIT</th><th>Cuenta</th><th>Estado</th><th>Neto autónomo</th><th>Neto histórico</th><th>Diferencia</th>
      </tr></thead><tbody>
        {(c.porBeneficiario || []).map((item) => <tr key={`b-${item.cuit}`}><td>Beneficiario</td><td>{item.cuit}</td><td>—</td><td>{item.estado}</td><td>{formatDecimal(item.netoAutonomo)}</td><td>{formatDecimal(item.netoHistorico)}</td><td>{centavos(item.diferenciaCentavos)}</td></tr>)}
        {(c.porCuenta || []).map((item) => <tr key={`c-${item.cuit}-${item.cuenta}`}><td>Cuenta</td><td>{item.cuit}</td><td>{item.cuenta}</td><td>{item.estado}</td><td>{formatDecimal(item.netoAutonomo)}</td><td>{formatDecimal(item.netoHistorico)}</td><td>{centavos(item.diferenciaCentavos)}</td></tr>)}
      </tbody></table></div>}

      {(c.operaciones.diferencias || []).length > 0 && <>
        <h4 className="pagos-subheading">Diferencias por operación (primeras {Math.min(100, c.operaciones.diferencias.length)})</h4>
        <div className="pagos-table-wrap"><table aria-label="Diferencias por operación"><thead><tr><th>Operación</th><th>Componente</th><th>Motor</th><th>Histórico</th><th>Diferencia</th><th>Regla</th></tr></thead><tbody>
          {c.operaciones.diferencias.slice(0, 100).map((item) => (
            <tr key={`${item.operacionId}-${item.componente}`}><td>{item.operacionId}</td><td>{COMPONENTE_LABELS[item.componente] || item.componente}</td>
              <td>{formatDecimal(item.motor)}</td><td>{formatDecimal(item.historico)}</td><td>{centavos(item.diferenciaCentavos)}</td><td>{item.regla}</td></tr>
          ))}
        </tbody></table></div>
      </>}
    </section>
  );
}

/**
 * FUR autónomo en simulación: el backend liquida la importación SAS con el motor, el snapshot
 * de Listas y las reglas aprobadas para pruebas, arma los grupos por beneficiario y compara con
 * el histórico. La pantalla sólo envía IDs; no paga, no genera TXT ni confirma obligaciones.
 */
export default function FurAutonomo({ token }) {
  const [estado, setEstado] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState("");
  const [fecha, setFecha] = useState("");
  const [importaciones, setImportaciones] = useState([]);
  const [importacionId, setImportacionId] = useState(null);
  const [diario, setDiario] = useState(null);
  const [corridas, setCorridas] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let vigente = true;
    Promise.resolve().then(() => pagosApi.estadoCondiciones(token))
      .then(async (response) => {
        if (!vigente) return;
        setEstado(response || { habilitada: false });
        if (response?.habilitada) {
          const lista = await pagosApi.snapshotsCondiciones(token);
          if (vigente) { setSnapshots(lista || []); setSnapshotId(lista?.[0]?.id || ""); }
        }
      })
      .catch((requestError) => { if (vigente) setEstado({ habilitada: false, motivo: requestError?.message }); });
    return () => { vigente = false; };
  }, [token]);

  const ejecutar = async (clave, accion) => {
    setError(""); setLoading(clave);
    try {
      await accion();
    } catch (requestError) {
      setError(requestError?.message || "No se pudo completar la operación.");
    } finally {
      setLoading("");
    }
  };

  const buscar = () => ejecutar("lotes", async () => {
    setResultado(null);
    const [lotes, previas] = await Promise.all([pagosApi.lotesSas(fecha, token), pagosApi.furCorridas(fecha, token)]);
    setImportaciones((lotes?.importaciones || []).filter((item) => item.aptaSas));
    setImportacionId(lotes?.importacionSugerida ?? null);
    setCorridas(previas || []);
  });

  const ver = (id) => ejecutar("ver", async () => setResultado(await pagosApi.furResultado(id, token)));

  const simular = () => ejecutar("simular", async () => {
    const corrida = await pagosApi.furSimular({ importacionId, snapshotId, diario }, token);
    setCorridas(await pagosApi.furCorridas(fecha, token) || []);
    setResultado(await pagosApi.furResultado(corrida.id, token));
  });

  if (!estado) return <section className="pagos-panel" data-testid="fur-autonomo"><p>Cargando…</p></section>;
  if (!estado.habilitada) {
    return (
      <section className="pagos-panel" data-testid="fur-autonomo">
        <h2>FUR autónomo — Simulación</h2>
        <div className="pagos-alert pagos-alert--warning" role="status">La simulación sólo funciona en el entorno aislado de desarrollo. {estado.motivo}</div>
      </section>
    );
  }

  return (
    <section className="pagos-panel" aria-labelledby="pagos-fur-autonomo-title" data-testid="fur-autonomo">
      <div className="pagos-section-heading">
        <div>
          <h2 id="pagos-fur-autonomo-title">FUR autónomo — Simulación</h2>
          <p>Netos calculados por el motor con reglas aprobadas para pruebas, agrupados por beneficiario y comparados con el Diario y el FUR históricos.</p>
        </div>
        <span className="pagos-status pagos-status--warning">Simulación · no paga ni genera TXT</span>
      </div>
      {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}

      <div className="pagos-filter-grid">
        <label className="pagos-date"><span>Fecha del lote</span>
          <input aria-label="Fecha del lote FUR autónomo" type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} /></label>
        <button type="button" className="pagos-secondary" disabled={!fecha || loading === "lotes"} onClick={buscar}>Buscar lote</button>
      </div>
      <div className="pagos-filter-grid">
        <label className="pagos-date"><span>Importación SAS</span>
          <select aria-label="Importación SAS" value={importacionId ?? ""} onChange={(event) => setImportacionId(Number(event.target.value) || null)}>
            <option value="">Elegí…</option>
            {importaciones.map((item) => <option key={item.id} value={item.id}>{item.id} · {item.archivo} · {item.filasInsertadas} filas</option>)}
          </select></label>
        <label className="pagos-date"><span>Snapshot de Listas</span>
          <select aria-label="Snapshot de Listas" value={snapshotId} onChange={(event) => setSnapshotId(event.target.value)}>
            {!snapshots.length && <option value="">Cargalo en Condiciones financieras</option>}
            {snapshots.map((item) => <option key={item.id} value={item.id}>{item.archivo}</option>)}
          </select></label>
        <label className="pagos-date"><span>Diario del lote (opcional, para comparar componentes)</span>
          <input aria-label="Diario para comparar" type="file" accept=".xlsx,.xlsm" onChange={(event) => setDiario(event.target.files?.[0] || null)} /></label>
        <button type="button" className="pagos-primary" disabled={!importacionId || !snapshotId || loading === "simular"} onClick={simular}>
          {loading === "simular" ? "Calculando…" : "Ejecutar cálculo autónomo"}</button>
      </div>

      {corridas.length > 0 && <>
        <h3 className="pagos-subheading">Corridas registradas</h3>
        <div className="pagos-table-wrap"><table aria-label="Corridas del FUR autónomo"><thead><tr>
          <th>Fecha</th><th>Importación</th><th>Estado</th><th>Veredicto</th><th>Operaciones</th><th>Neto autónomo</th><th>Neto histórico</th><th>Registró</th><th></th>
        </tr></thead><tbody>
          {corridas.map((item) => (
            <tr key={item.id}><td>{formatFecha(item.fechaLote)}</td><td>{item.importacionId}</td><td>{item.estado}</td><td><Veredicto valor={item.veredicto} /></td>
              <td>{item.operaciones - item.incompletas}/{item.operaciones}</td><td>{item.netoAutonomo === null ? "Bloqueado" : formatDecimal(String(item.netoAutonomo))}</td>
              <td>{formatDecimal(String(item.netoHistorico))}</td><td>{item.creadoPor}<small className="pagos-cell-note">huella {String(item.huella).slice(0, 10)}</small></td>
              <td><button type="button" className="pagos-secondary" onClick={() => ver(item.id)}>Ver</button></td></tr>
          ))}
        </tbody></table></div>
      </>}

      {resultado && <Resultado resultado={resultado} />}
    </section>
  );
}
